import { Component, ElementRef, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SvgIllustration, SvgModule } from '@ui/svg';
import { TranslateModule } from '@ngx-translate/core';
import { WaterTransferAsset } from '@app/water/interfaces/water-transfer-asset.interface';
import { ButtonAppearance, ButtonModule } from '@ui/button';
import { AsyncPipe, NgClass, NgForOf, NgTemplateOutlet } from '@angular/common';
import { WaterModule } from '@app/water/water.module';
import { WaterTransferAssetType } from '@ui/water/enums/water-transfer-asset-type.enum';
import { FormatDatePipe, ModalSandbox, NotificationSandbox, PipeModule, UiContext } from '@smooved/ui';
import { ALLOWED_MIME_TYPES, ArrayUtils, Asset, DbUtils, FileUtils, inject, MAX_FILE_SIZE, Mimetypes, ObjectUtils } from '@smooved/core';
import { FileInput } from '@app/form/interfaces/file-input';
import { FileUploadModule } from 'ng2-file-upload';
import { WaterTransferAssetDetailComponent } from '@app/real-estate-agent/modals/water-transfer-asset-detail/water-transfer-asset-detail.component';
import { FileModalData } from '@app/real-estate-agent/interfaces/file-modal-data';
import { AuthenticationSandbox } from '@app/authentication/sandboxes/authentication.sandbox';
import { ServiceStatusIconComponent } from '@app/services/components/service-status-icon/service-status-icon.component';
import { StatusBadgeStatus } from '@ui-v2/atoms/status-badge/status-badge.constants';
import { WaterTransferDocumentServiceStore } from '@app/services/water-transfer-document/store/water-transfer-document-service.store';

@Component({
    selector: 'app-water-assets-form',
    standalone: true,
    imports: [
        FormsModule,
        SvgModule,
        TranslateModule,
        ReactiveFormsModule,
        ButtonModule,
        NgForOf,
        WaterModule,
        FormatDatePipe,
        FileUploadModule,
        NgClass,
        PipeModule,
        NgTemplateOutlet,
        AsyncPipe,
        ServiceStatusIconComponent,
    ],
    template: `
        <div
            [ngClass]="{
                'u-background-gray-ultralight': context === 'primary',
                'u-border-gray-ultralight u-background-white u-border-width-2': context === 'secondary',
            }"
            class="u-border-radius-large p-4 h-full"
        >
            <div class="u-margin-bottom">
                <div class="u-flex-row u-flex-justify-content-space-between">
                    <div class="u-flex-row u-flex-align-items-center">
                        @if (showServiceStatusIcon) {
                            <app-service-status-icon class="pr-2" [status]="serviceStatusIcon"></app-service-status-icon>
                        }
                        <h3 class="u-font-weight-semi-bold u-color-gray-ultradark u-font-size-20">
                            {{ title }}
                        </h3>
                    </div>
                    <ng-content select="[header-right]"></ng-content>
                </div>
                @if (descriptionTemplate) {
                    <p class="u-color-muted u-margin-top-half">
                        <ng-container *ngTemplateOutlet="descriptionTemplate"></ng-container>
                    </p>
                }
            </div>
            <div class="u-flex-column u-margin-top">
                <div
                    class="u-flex-row u-padding-half u-padding-right-double u-border-radius u-bordered-gray-lightest u-margin-bottom-half u-flex-align-items-center __attachment-row"
                    *ngFor="
                        let waterTransferAsset of waterTransferDocumentServiceStore.waterTransferDocumentService()?.waterTransferAssets
                            | waterTransferAssetFilter: assetTypesToFilter()
                    "
                >
                    <smvd-ui-svg-illustration
                        class="u-margin-right u-flex-no-shrink __mime-icon"
                        [svg]="waterTransferAsset?.mime === Mimetypes.Pdf ? svgIllustration.PdfFile : svgIllustration.ImageFile"
                    ></smvd-ui-svg-illustration>
                    <div class="u-flex-column u-flex-grow-1 u-cursor-pointer __file-info" (click)="openDetail(waterTransferAsset)">
                        <div class="u-color-gray-ultradark __file-name">{{ waterTransferAsset?.name }}</div>
                        <div class="u-font-size-12 u-color-gray-dark">{{ waterTransferAsset?.createdOn | formatDate }}</div>
                    </div>
                    @if (!hideRemove || (authenticationSandbox.isAdmin$ | async)) {
                        <a
                            (click)="removeAsset(waterTransferAsset)"
                            app-svg-illustration
                            class="u-color-gray-dark u-cursor-pointer u-flex-no-shrink u-margin-left-half"
                            [svg]="svgIllustration.CloseButton"
                        ></a>
                    }
                </div>
                <div class="u-flex-row">
                    <button
                        id="submit-button"
                        smvd-ui-button
                        [disabled]="buttonDisabled"
                        [appearance]="buttonAppearance.Stroked"
                        [context]="uiContext.PrimaryDark"
                        (click)="handleFileInputClick()"
                    >
                        {{ buttonLabel }}
                    </button>
                    <ng-content select="[buttons]"></ng-content>
                </div>
            </div>
        </div>
        <input
            #fileInput
            hidden
            [accept]="ALLOWED_MIME_TYPES"
            type="file"
            ng2FileSelect
            (onFileSelected)="onFileSelected($event)"
            [uploader]="uploader"
            multiple
        />
    `,
    styleUrl: 'water-assets-form.component.scss',
})
export class WaterAssetsFormComponent {
    @ViewChild('fileInput') fileInput: ElementRef;
    @Input() public transactionId: string;
    @Input() public assetType: WaterTransferAssetType;
    @Input() public title: string;
    @Input() public descriptionTemplate: TemplateRef<any>;
    @Input() public buttonLabel: string;
    @Input() public buttonDisabled: boolean;
    @Input() public hideRemove: boolean;
    @Input() public context: 'primary' | 'secondary' = 'primary';
    @Input() public showServiceStatusIcon: boolean = false;
    @Input() public serviceStatusIcon: StatusBadgeStatus;

    @Output() public assetUploaded: EventEmitter<void> = new EventEmitter<void>();
    @Output() public assetDeleted: EventEmitter<void> = new EventEmitter<void>();

    public readonly waterTransferDocumentServiceStore = inject(WaterTransferDocumentServiceStore);
    public readonly notificationSandbox = inject(NotificationSandbox);
    public readonly modalSandbox = inject(ModalSandbox);
    public readonly authenticationSandbox = inject(AuthenticationSandbox);

    protected readonly uiContext = UiContext;
    protected readonly buttonAppearance = ButtonAppearance;
    public readonly ALLOWED_MIME_TYPES = ALLOWED_MIME_TYPES;
    public uploader = FileUtils.getFileUploader();

    protected readonly Mimetypes = Mimetypes;
    protected readonly svgIllustration = SvgIllustration;

    public assetTypesToFilter(): WaterTransferAssetType[] {
        if (!ObjectUtils.isDefined(this.assetType)) return [];
        if ((this.assetType as WaterTransferAssetType) === WaterTransferAssetType.Attachment)
            return [WaterTransferAssetType.Attachment, undefined];
        return [this.assetType];
    }

    public openDetail(file: FileInput): void {
        this.modalSandbox.openModal(
            null,
            null,
            null,
            WaterTransferAssetDetailComponent,
            { data: this.modalDataFactory(file as Asset) },
            null,
        );
    }

    public handleFileInputClick(): void {
        this.fileInput.nativeElement.click();
    }

    public async removeAsset(waterTransferAsset: WaterTransferAsset): Promise<void> {
        await this.waterTransferDocumentServiceStore.updateWaterTransferAssets(
            DbUtils.getStringId(this.transactionId),
            [waterTransferAsset.key],
            [],
            waterTransferAsset.waterTransferAssetType || WaterTransferAssetType.Attachment,
        );
        this.assetDeleted.emit();
    }

    public async onFileSelected(fileList: File[]): Promise<void> {
        const list = Array.from(fileList);
        const maxSize = list.find((file) => file.size > FileUtils.convertMegaBytesToBytes(MAX_FILE_SIZE));
        if (maxSize) {
            this.notificationSandbox.error('ENERGY.INPUT.FILE_ERROR.SIZE', { maxFileSize: MAX_FILE_SIZE });
            return;
        }

        const noAllowedType = list.find((file) => !ALLOWED_MIME_TYPES.includes(file.type as Mimetypes));
        if (noAllowedType) {
            this.notificationSandbox.error('ENERGY.INPUT.FILE_ERROR.TYPE', {
                allowedTypes: ALLOWED_MIME_TYPES.map((mime) => ArrayUtils.last(mime.split('/'))).join(', '),
            });
            return;
        }

        await this.waterTransferDocumentServiceStore.updateWaterTransferAssets(this.transactionId, [], list, this.assetType);
        this.assetUploaded.emit();
    }

    private modalDataFactory(file: Asset): FileModalData {
        return {
            moveId: this.transactionId,
            readOnly: true,
            file,
        };
    }
}
